import _ from 'lodash';
import PoisActions from 'states/resources/pois/actions';

import { draftToPayloadPOIv2 } from './services/draft-to-payload';
import { validatePois, validateInterpolatedPois, constructPois } from './services/csv-processing';
import { interpolatePoints } from './services/async-csv-processing';

import GeoCategories from 'states/resources/geo-categories/actions';

const actions = {
    setUiErrors(uiErrors) {
        return {
            type: 'POIS_EDITOR__SET_UI_ERRORS',
            payload: {
                uiErrors,
            },
        };
    },
    setPointsError(poiErrors) {
        return {
            type: 'POIS_EDITOR__SET_POINTS_ERROR',
            payload: {
                poiErrors,
            },
        };
    },
    setFileTypeError(files) {
        return {
            type: 'POIS_EDITOR__SET_FILE_TYPE_ERROR',
            payload: {
                files,
            },
        };
    },
    initialize(poiId, type) {
        return (dispatch, getState) => {
            dispatch({
                type: 'POIS_EDITOR__INIT_START',
                payload: {
                    poiId,
                },
            });

            dispatch(GeoCategories.getCategories({ allowCategoriesWithoutLayers: true })).then(
                geoCategories => {
                    if (!poiId) {
                        const organizationId =
                            type === 'custom' ? _.get(getState(), `profile.organizationId`) : null;

                        dispatch(actions.setPointsError(['A file must be uploaded']));

                        dispatch({
                            type: 'POIS_EDITOR__INIT_END',
                            payload: {
                                poi: null,
                                organizationId,
                                geoCategories,
                            },
                        });
                        return;
                    }

                    // fetch existing POI and load into draft
                    dispatch(PoisActions.getOne(poiId)).then(({ points, ...rest }) => {
                        const organizationId = _.get(getState(), `profile.organizationId`);

                        dispatch({
                            type: 'POIS_EDITOR__INIT_END',
                            payload: {
                                poiId,
                                poi: rest,
                                points,
                                organizationId,
                                geoCategories,
                            },
                        });
                    });
                }
            );
        };
    },
    updateLayer(field, value) {
        return {
            type: 'POIS_EDITOR__UPDATE_LAYER',
            payload: {
                [field]: value,
            },
        };
    },
    updateDraft(changes) {
        return {
            type: 'POIS_EDITOR__UPDATE_DRAFT',
            payload: changes,
        };
    },
    removePoint(pointToRemove) {
        return {
            type: 'POIS_EDITOR__REMOVE_POINT',
            payload: { pointToRemove },
        };
    },
    processUploadedPoints(formData, options = {}) {
        return dispatch => {
            const file = formData.get('asset');
            const reader = new FileReader();

            const onLoadEnd = async (file, reader) => {
                let mergedErrors = {};
                let errors = validatePois({ data: reader.result, parseCsv: true });
                let interpolateErrors = {};

                if (Array.isArray(errors)) {
                    dispatch({
                        type: 'POIS_EDITOR__PROCESS_POINTS__FAILURE',
                        payload: {
                            errors: {},
                            uiErrors: [errors.join(' ')],
                        },
                    });
                    return;
                }

                if (Object.keys(errors).length > 0) {
                    dispatch({
                        type: 'POIS_EDITOR__PROCESS_POINTS__FAILURE',
                        payload: { errors },
                    });
                }

                let pointsFromCsv = constructPois(reader.result);

                try {
                    dispatch({
                        type: 'POIS_EDITOR__INTERPOLATE_POINTS__START',
                        payload: {},
                    });

                    pointsFromCsv = await interpolatePoints(pointsFromCsv);

                    interpolateErrors = validateInterpolatedPois({ data: pointsFromCsv });

                    dispatch({
                        type: 'POIS_EDITOR__INTERPOLATE_POINTS__END',
                        payload: {},
                    });
                } catch (error) {
                    dispatch({
                        type: 'POIS_EDITOR__PROCESS_POINTS__FAILURE',
                        payload: {
                            errors: {},
                            uiErrors: [
                                'Unable to process uploaded CSV. Please check the file format and try again.',
                            ],
                        },
                    });
                    return;
                }

                const uniquePoints = _.uniqWith(pointsFromCsv, (pointA, pointB) => {
                    const pointACoords = `${pointA.latitude},${pointA.longitude}`;
                    const pointBCoords = `${pointB.latitude},${pointB.longitude}`;

                    // 1. Check for empty/invalid coordinates
                    const pointA_Invalid = !pointA.latitude || !pointA.longitude;
                    const pointB_Invalid = !pointB.latitude || !pointB.longitude;

                    // If either point is invalid (has empty lat/lon), they should be treated as unique
                    // unless they are both invalid and identical (e.g., both are {lat: "", lon: ""}).
                    // However, for typical geographic data cleansing, we usually want to KEEP the
                    // invalid ones separate so they can be logged or corrected later.

                    // If you want to IGNORE comparison if either point is invalid, return false (not equal):
                    if (pointA_Invalid || pointB_Invalid) {
                        // Returning false here means they are NOT considered duplicates of each other,
                        // ensuring both potentially invalid points remain in the output array.
                        return false;
                    }

                    // 2. If both are valid, proceed with the strict coordinate string comparison
                    return pointACoords === pointBCoords;
                });

                const hasDuplicates = pointsFromCsv.length !== uniquePoints.length;
                mergedErrors = _.merge({}, errors, interpolateErrors);

                if (uniquePoints.length === 0) {
                    dispatch({
                        type: 'POIS_EDITOR__PROCESS_POINTS__FAILURE',
                        payload: {
                            errors: {},
                            uiErrors: ['No rows were found in the uploaded CSV file.'],
                        },
                    });
                    return;
                }

                dispatch({
                    type: 'POIS_EDITOR__PROCESS_POINTS__SUCCESS',
                    payload: {
                        points: uniquePoints,
                        options,
                        errors: mergedErrors,
                        hasDuplicates,
                    },
                });
            };

            dispatch({
                type: 'POIS_EDITOR__PROCESS_POINTS',
                payload: { fileName: file.name },
            });

            reader.addEventListener('loadend', () => onLoadEnd(file, reader));

            reader.readAsText(file);
        };
    },
    save() {
        return (dispatch, getState) => {
            dispatch({
                type: 'POIS_EDITOR__SAVE_START',
                payload: {},
            });

            const {
                poiErrors,
                metadataErrors,
                isEditing,
                poiId,
                draft,
                cleanlistErrors,
                csvErrors,
            } = _.get(getState(), 'poisEditor');

            if (draft.source !== 'cleanlist' && poiErrors.length > 0) {
                dispatch(
                    actions.setUiErrors(['There are errors in the form under tab UPLOAD POI LIST'])
                );
                return Promise.reject(
                    new Error('There are errors in the form under tab UPLOAD POI LIST')
                );
            }
            if (draft.source !== 'cleanlist' && csvErrors && Object.keys(csvErrors).length > 0) {
                dispatch(actions.setUiErrors(['There are errors in the uploaded CSV file']));
                return Promise.reject(new Error('There are errors in the uploaded csv file'));
            }
            if (draft.source === 'uploaded' && (!draft.points || draft.points.length === 0)) {
                dispatch(actions.setUiErrors(['No rows were found in the uploaded CSV file.']));
                return Promise.reject(new Error('No rows were found in the uploaded CSV file.'));
            }
            if (metadataErrors.length > 0) {
                dispatch(actions.setUiErrors(['There are metadata errors in the form']));
                return Promise.reject(new Error('There are metadata errors in the form'));
            }
            if (draft.source === 'cleanlist' && cleanlistErrors.length > 0) {
                dispatch(
                    actions.setUiErrors(['There are errors in the form under tab SEARCH FOR POIS'])
                );
                return Promise.reject(
                    new Error('There are errors in the form under tab SEARCH FOR POIS')
                );
            }

            dispatch(actions.setUiErrors([]));

            let payload = draftToPayloadPOIv2(draft);

            /**
             * We don´t want to save points with latitude and longitude 0
             * The users receive an alert saying that the points are invalid
             * and they can´t be saved
             */
            if (payload.source === 'uploaded') {
                payload.points = payload.points.filter(
                    point => point.latitude !== 0 && point.longitude !== 0
                );

                if (payload.points.length === 0) {
                    dispatch(actions.setUiErrors(['No rows were found in the uploaded CSV file.']));
                    return Promise.reject(
                        new Error('No rows were found in the uploaded CSV file.')
                    );
                }
            }

            let operation;

            if (isEditing) {
                operation = dispatch(PoisActions.updatePoi(payload, poiId));
            } else {
                operation = dispatch(PoisActions.createPoi(payload));
            }

            return operation
                .then(() => {
                    dispatch({
                        type: 'POIS_EDITOR__SAVE_END',
                        payload: {},
                    });
                })
                .catch(err => {
                    let parsedServerErrors;
                    try {
                        parsedServerErrors = JSON.parse(err.bodyText);
                    } catch (parseError) {
                        parsedServerErrors = null;
                    }

                    dispatch({
                        type: 'POIS_EDITOR__SAVE_SERVER_ERROR',
                        payload: {
                            err: parsedServerErrors,
                        },
                    });

                    dispatch(actions.setUiErrors(['Unable to save segment. Please try again.']));
                    return Promise.reject(new Error('Server save failed'));
                });
        };
    },
};

export default actions;
